import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router';
import { loadLocaleMessages } from '@/plugins/i18n';

import AppLayout from '@/layouts/AppLayout.vue';
import PublicLayout from '@/layouts/PublicLayout.vue';
import LoginLayout from '@/layouts/LoginLayout.vue';
import KioskLayout from '@/layouts/KioskLayout.vue';

const DashboardPage = () => import('@/pages/DashboardPage.vue');
const SitdownPage = () => import('@/pages/SitdownPage.vue');
const ProjectsPage = () => import('@/pages/ProjectsPage.vue');
const AbsencesPage = () => import('@/pages/AbsencesPage.vue');
const GamePage = () => import('@/pages/GamePage.vue');
const AchievementsPage = () => import('@/pages/AchievementsPage.vue');
const WorklogsPage = () => import('@/pages/WorklogsPage.vue');
const UsersPage = () => import('@/pages/UsersPage.vue');
const PositionsPage = () => import('@/pages/PositionsPage.vue');
const FeedbackHeatmapPage = () => import('@/pages/FeedbackHeatmapPage.vue');
const SitdownTimesPage = () => import('@/pages/SitdownTimesPage.vue');
const SettingsPage = () => import('@/pages/SettingsPage.vue');
const UserSettingsPage = () => import('@/pages/UserSettingsPage.vue');
const UserGoalsPage = () => import('@/pages/UserGoalsPage.vue');
const ColleaguesPage = () => import('@/pages/ColleaguesPage.vue');
const ColleaguesDetailPage = () => import('@/pages/ColleaguesDetailPage.vue');
const SubmitFeedbackPage = () => import('@/pages/public/SubmitFeedbackPage.vue');
const ApproveAbsencePage = () => import('@/pages/public/ApproveAbsencePage.vue');
const AbsencesApprovingPage = () => import('@/pages/AbsencesApprovingPage.vue');
const SubstituteGroupsPage = () => import('@/pages/SubstituteGroupsPage.vue');
const securityPage = () => import('@/pages/SecurityPage.vue');
const DocumentationPage = () => import('@/pages/DocumentationPage.vue');
const DocumentsPage = () => import('@/pages/DocumentsPage.vue');
const DocumentCreatePage = () => import('@/pages/DocumentCreatePage.vue');
const DevMetricsPage = () => import('@/pages/DevMetricsPage.vue');
const PclDxMetricsPage = () => import('@/pages/PclDxMetricsPage.vue');
const MetabaseMetricsPage = () => import('@/pages/MetabaseMetricsPage.vue');
const PmMetricsPage = () => import('@/pages/PmMetricsPage.vue');
const KioskPairingPage = () => import('@/pages/kiosk/KioskPairingPage.vue');
const KioskMetabasePage = () => import('@/pages/kiosk/KioskMetabasePage.vue');
const WorkingTimeRecordsPage = () => import('@/pages/WorkingTimeRecordsPage.vue');
const WorkingTimeRecordsAdministrationPage = () => import('@/pages/WorkingTimeRecordsAdministrationPage.vue');

import { usePage } from '@/composables/usePage';
import { PAGE_NAMES } from '@/constants/pages';
import User1on1sPage from '@/pages/User1on1sPage.vue';
import CollaboratorHistoryPage from '@/pages/CollaboratorHistoryPage.vue';
import { useAuthStore } from '@/store/auth';
import { PermissionsDefinition } from '@/store/modules/PositionModule';
import { useAuthorization } from '@/composables/auth/useAuthorization';
import CollaboratorNotesPage from '@/pages/CollaboratorNotesPage.vue';
import { useAuthentication } from '@/composables/auth/useAuthentication';

const { pages } = usePage();

declare module 'vue-router' {
  interface RouteMeta {
    isPublicRoute?: boolean;
    authorize: PermissionsDefinition[];
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: PAGE_NAMES.INDEX,
    component: LoginLayout,
    meta: {
      authorize: pages[PAGE_NAMES.INDEX].permission,
    },
  },

  {
    path: '/public',
    name: PAGE_NAMES.PUBLIC,
    component: PublicLayout,
    meta: {
      isPublicRoute: true,
      authorize: pages[PAGE_NAMES.PUBLIC].permission,
    },
    children: [
      {
        path: 'submit-feedback',
        name: PAGE_NAMES.SUBMIT_FEEDBACK,
        component: SubmitFeedbackPage,
        meta: {
          isPublicRoute: true,
          authorize: pages[PAGE_NAMES.SUBMIT_FEEDBACK].permission,
        },
      },
      {
        path: 'approve-absence',
        name: PAGE_NAMES.APPROVE_ABSENCE,
        component: ApproveAbsencePage,
        meta: {
          isPublicRoute: true,
          authorize: pages[PAGE_NAMES.APPROVE_ABSENCE].permission,
        },
      },
    ],
  },
  {
    path: '/app',
    name: PAGE_NAMES.APP,
    component: AppLayout,
    meta: {
      authorize: pages[PAGE_NAMES.APP].permission,
    },
    children: [
      {
        path: 'dashboard',
        name: PAGE_NAMES.DASHBOARD,
        component: DashboardPage,
        meta: {
          authorize: pages[PAGE_NAMES.DASHBOARD].permission,
        },
      },
      {
        path: 'sitdown',
        name: PAGE_NAMES.SITDOWN,
        component: SitdownPage,
        meta: {
          authorize: pages[PAGE_NAMES.SITDOWN].permission,
        },
      },
      {
        path: 'projects',
        name: PAGE_NAMES.PROJECTS,
        component: ProjectsPage,
        meta: {
          authorize: pages[PAGE_NAMES.PROJECTS].permission,
        },
      },
      {
        path: 'absences',
        name: PAGE_NAMES.ABSENCES,
        component: AbsencesPage,
        meta: {
          authorize: pages[PAGE_NAMES.ABSENCES].permission,
        },
      },
      {
        path: 'absences-approving',
        name: PAGE_NAMES.ABSENCES_APPROVING,
        component: AbsencesApprovingPage,
        meta: {
          authorize: pages[PAGE_NAMES.ABSENCES_APPROVING].permission,
        },
      },
      {
        path: 'game',
        name: PAGE_NAMES.GAME,
        component: GamePage,
        meta: {
          authorize: pages[PAGE_NAMES.GAME].permission,
        },
      },
      {
        path: 'achievements',
        name: PAGE_NAMES.ACHIEVEMENTS,
        component: AchievementsPage,
        meta: {
          authorize: pages[PAGE_NAMES.ACHIEVEMENTS].permission,
        },
      },
      {
        path: 'worklogs',
        name: PAGE_NAMES.WORKLOGS,
        component: WorklogsPage,
        meta: {
          authorize: pages[PAGE_NAMES.WORKLOGS].permission,
        },
      },
      {
        path: 'users',
        name: PAGE_NAMES.USERS,
        component: UsersPage,
        meta: {
          authorize: pages[PAGE_NAMES.USERS].permission,
        },
      },
      {
        path: 'positions',
        name: PAGE_NAMES.POSITIONS,
        component: PositionsPage,
        meta: {
          authorize: pages[PAGE_NAMES.POSITIONS].permission,
        },
      },
      {
        path: 'feedback-heatmap',
        name: PAGE_NAMES.FEEDBACK_HEATMAP,
        component: FeedbackHeatmapPage,
        meta: {
          authorize: pages[PAGE_NAMES.FEEDBACK_HEATMAP].permission,
        },
      },
      {
        path: 'sitdown-times',
        name: PAGE_NAMES.SITDOWN_TIMES,
        component: SitdownTimesPage,
        meta: {
          authorize: pages[PAGE_NAMES.SITDOWN_TIMES].permission,
        },
      },
      {
        path: 'my-goals',
        name: PAGE_NAMES.USER_GOALS,
        component: UserGoalsPage,
        meta: {
          authorize: pages[PAGE_NAMES.USER_GOALS].permission,
        },
      },
      {
        path: 'user-1on1',
        name: PAGE_NAMES.USER_1ON1S,
        component: User1on1sPage,
        meta: {
          authorize: pages[PAGE_NAMES.USER_1ON1S].permission,
        },
      },
      {
        path: 'collaborator-notes',
        name: PAGE_NAMES.COLLABORATOR_NOTES,
        component: CollaboratorNotesPage,
        meta: {
          authorize: pages[PAGE_NAMES.COLLABORATOR_NOTES].permission,
        },
      },
      {
        path: 'collaborator-history',
        name: PAGE_NAMES.COLLABORATOR_HISTORY,
        component: CollaboratorHistoryPage,
        meta: {
          authorize: pages[PAGE_NAMES.COLLABORATOR_HISTORY].permission,
        },
      },
      {
        path: 'colleagues',
        name: PAGE_NAMES.COLLEAGUES,
        component: ColleaguesPage,
        meta: {
          authorize: pages[PAGE_NAMES.COLLEAGUES].permission,
        },
      },
      {
        path: 'colleagues/:uuid',
        name: PAGE_NAMES.COLLEAGUES_DETAIL,
        component: ColleaguesDetailPage,
        meta: {
          authorize: pages[PAGE_NAMES.COLLEAGUES_DETAIL].permission,
        },
      },
      {
        path: 'settings',
        name: PAGE_NAMES.SETTINGS,
        component: SettingsPage,
        meta: {
          authorize: pages[PAGE_NAMES.SETTINGS].permission,
        },
      },
      {
        path: 'user-settings',
        name: PAGE_NAMES.USER_SETTINGS,
        component: UserSettingsPage,
        meta: {
          authorize: pages[PAGE_NAMES.USER_SETTINGS].permission,
        },
      },
      {
        path: 'security',
        name: PAGE_NAMES.SECURITY_TF,
        component: securityPage,
        meta: {
          authorize: pages[PAGE_NAMES.SECURITY_TF].permission,
        },
      },
      {
        path: 'documentation',
        name: PAGE_NAMES.DOCUMENTATION,
        component: DocumentationPage,
        meta: {
          authorize: pages[PAGE_NAMES.DOCUMENTATION].permission,
        },
      },
      {
        path: 'substitute-groups',
        name: PAGE_NAMES.SUBSTITUTE_GROUPS,
        component: SubstituteGroupsPage,
        meta: {
          authorize: pages[PAGE_NAMES.SUBSTITUTE_GROUPS].permission,
        },
      },
      {
        path: 'documents',
        name: PAGE_NAMES.DOCUMENTS,
        component: DocumentsPage,
        meta: {
          authorize: pages[PAGE_NAMES.DOCUMENTS].permission,
        },
      },
      {
        path: 'documents-create',
        name: PAGE_NAMES.DOCUMENTS_CREATE,
        component: DocumentCreatePage,
        meta: {
          authorize: pages[PAGE_NAMES.DOCUMENTS_CREATE].permission,
        },
      },
      {
        path: 'dev-metrics',
        name: PAGE_NAMES.DEV_METRICS,
        component: DevMetricsPage,
        meta: {
          authorize: pages[PAGE_NAMES.DEV_METRICS].permission,
        },
      },
      {
        path: 'pcldx-metrics',
        name: PAGE_NAMES.PCLDX_METRICS,
        component: PclDxMetricsPage,
        meta: {
          authorize: pages[PAGE_NAMES.PCLDX_METRICS].permission,
        },
      },
      {
        path: 'sprint-status',
        name: PAGE_NAMES.METABASE_METRICS,
        component: MetabaseMetricsPage,
        meta: {
          authorize: pages[PAGE_NAMES.METABASE_METRICS].permission,
        },
      },
      {
        path: 'pm-metrics',
        name: PAGE_NAMES.PM_METRICS,
        component: PmMetricsPage,
        meta: {
          authorize: pages[PAGE_NAMES.PM_METRICS].permission,
        },
      },
      {
        path: 'working-time-records',
        name: PAGE_NAMES.WORKING_TIME_RECORDS,
        component: WorkingTimeRecordsPage,
        meta: {
          authorize: pages[PAGE_NAMES.WORKING_TIME_RECORDS].permission,
        },
      },
      {
        path: 'working-time-records-administration',
        name: PAGE_NAMES.WORKING_TIME_RECORDS_ADMINISTRATION,
        component: WorkingTimeRecordsAdministrationPage,
        meta: {
          authorize: pages[PAGE_NAMES.WORKING_TIME_RECORDS_ADMINISTRATION].permission,
        },
      },
    ],
  },
  {
    path: '/kiosk',
    component: KioskLayout,
    meta: {
      isPublicRoute: true,
      authorize: [],
    },
    children: [
      {
        path: 'pairing',
        name: 'kiosk-pairing',
        component: KioskPairingPage,
        meta: { isPublicRoute: true, authorize: [] },
      },
      {
        path: 'colleagues',
        name: 'kiosk-colleagues',
        component: ColleaguesPage,
        meta: { isPublicRoute: true, authorize: [] },
      },
      {
        path: 'dev-metrics',
        name: 'kiosk-dev-metrics',
        component: DevMetricsPage,
        meta: { isPublicRoute: true, authorize: [] },
      },
      {
        path: 'sprint-status',
        name: 'kiosk-sprint-status',
        component: MetabaseMetricsPage,
        meta: { isPublicRoute: true, authorize: [] },
      },
      {
        path: 'metabase/:reportId',
        name: 'kiosk-metabase',
        component: KioskMetabasePage,
        meta: { isPublicRoute: true, authorize: [] },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});
// eslint-disable-next-line sonarjs/no-inconsistent-returns
router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const { hasLoggedUserPermission } = useAuthorization();
  const { logout } = useAuthentication();

  const { authorize, isPublicRoute } = to.meta;

  if (authStore.isLoading && !isPublicRoute) {
    await authStore.waitForAuth();
  }
  const locale = authStore.currentUser?.language ?? localStorage.getItem('locale');
  await loadLocaleMessages(locale);

  if (authStore.isLogged && (to.name === PAGE_NAMES.INDEX || to.name === PAGE_NAMES.LOGIN)) {
    return { name: PAGE_NAMES.DASHBOARD };
  }

  if (isPublicRoute) {
    authStore.setLoading(false);
    return;
  }

  const allowedRoutes = [
    PAGE_NAMES.LOGIN.toString(),
    PAGE_NAMES.APP.toString(),
    PAGE_NAMES.SECURITY_TF.toString(),
    PAGE_NAMES.USER_SETTINGS.toString(),
  ];
  const routeTo = typeof to.name === 'string' ? to.name : '';

  const currentUser = authStore.currentAgwUser;

  const hasPermission = () => {
    const permissions = currentUser?.mappedPermissions;

    if (permissions) {
      return authorize.length ? authorize.some(hasLoggedUserPermission) : true;
    }

    return false;
  };

  if (!isPublicRoute && !authStore.isLogged) {
    console.log('[router] redirecting to login page');
    await logout(false);
  } else if (allowedRoutes.includes(routeTo)) {
    return;
  } else if (authorize && hasPermission()) {
    return;
  } else {
    return { name: PAGE_NAMES.USER_SETTINGS };
  }
});

export default router;
